/**
 * Vehicle card extraction service — AI-powered tarjeta de circulación scanning.
 * Uses Claude Vision API via Supabase Edge Function.
 */
import { ImageManipulator } from 'expo-image-manipulator'
import { signedUrl } from '../utils/storage-url'
import * as Crypto from 'expo-crypto'
import { Platform } from 'react-native'
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'
import {
  assertObjectStored,
  bytesToBase64,
  compressImage,
  readUriBytes,
} from '../utils/image-compress'
import { uploadBytesWithSessionRecovery } from '../utils/storage-verify'
import { validIsoDate } from '../utils/dates'
import type {
  ExtractedInsurancePolicy,
  ExtractedVehicleCard,
} from '../types/vehicle-document.types'

type VerifiedDocumentType =
  | 'tarjeta_circulacion_front'
  | 'tarjeta_circulacion_back'
  | 'insurance_policy'

interface VerifyDocumentInput {
  vehicleId: string
  photoUri: string
  documentType: VerifiedDocumentType
  captureSource?: 'tago_live_camera'
  capturedAt?: string
  fileName?: string
  mimeType?: string
  expected?: {
    plateNumber?: string
    vin?: string
    year?: number
    make?: string
    model?: string
    expiryDate?: string
  }
}

type DocumentPayloadMimeType =
  | 'image/jpeg'
  | 'image/png'
  | 'image/gif'
  | 'image/webp'
  | 'application/pdf'

interface DocumentPayload {
  base64: string
  mimeType: DocumentPayloadMimeType
  filename?: string
}

interface EdgeFunctionErrorBody {
  message: string | null
  reasons: string[]
  statusCode?: number
}

interface PendingDocumentInput {
  vehicleId: string
  photoUri: string
  documentType: VerifiedDocumentType
  captureSource?: 'tago_live_camera'
  capturedAt?: string
  fileName?: string
  mimeType?: string
  documentNumber?: string | null
  expiryDate?: string | null
  status?: 'pending' | 'approved'
}

/**
 * A tarjeta de circulación is a LANDSCAPE document. The host's phone is not.
 *
 * Nothing here used to normalise orientation — `resize({ width })` scales, it
 * does not rotate — so a card photographed with the phone held upright reached
 * the vision model lying on its side. The model then returns a partial or empty
 * extraction, the document is filed `pending` instead of `approved`, and the
 * vehicle can never satisfy the activation gate. The host did nothing wrong and
 * gets no way to find that out.
 *
 * Rotating to landscape is deterministic, but WHICH way is not: a portrait frame
 * is equally consistent with a card turned 90° or 270°. So this returns the
 * candidate orientations in order of likelihood, and the caller re-asks with the
 * next one when an extraction comes back empty. Two calls at most, and only for
 * the images that actually failed.
 */
async function orientationCandidates(photoUri: string): Promise<number[]> {
  try {
    // Dimension probe only — encoding goes through compressImage.
    const probe = await ImageManipulator.manipulate(photoUri).renderAsync()
    // Already landscape: try as-is, then upside down, then both sideways.
    // A landscape buffer can still be a sideways card (locked orientation).
    if (probe.width >= probe.height) return [0, 180, -90, 90]
    // Portrait: the landscape card is on its side. -90 first — phone upright,
    // card turned clockwise to fill the frame. Then the other three.
    return [-90, 90, 0, 180]
  } catch {
    // Unreadable dimensions must never cost us the upload: send it as-is.
    return [0]
  }
}

/**
 * Did the model actually read the card, or just see ink?
 *
 * A sideways card usually returns structure with every identifying field null —
 * which the old code accepted as success, filed as a `pending` document, and
 * left the host to wonder about. Any ONE of these fields means the orientation
 * was good enough; none of them means try the other way up.
 *
 * `wrongDocument` counts as usable on purpose: the model recognised a licence or
 * a DUI, which is a real answer the host needs to hear, not a failed read.
 */
function hasUsableVehicleCardFields(extracted: ExtractedVehicleCard): boolean {
  if (extracted.wrongDocument) return true
  return Boolean(
    extracted.plateNumber ||
    extracted.vin ||
    extracted.chassisNumber ||
    extracted.tarjetaNumber ||
    extracted.ownerName ||
    (extracted.make && extracted.model),
  )
}

async function photoToBase64(photoUri: string, width: number, rotateDegrees = 0): Promise<string> {
  const { bytes } = await compressImage(
    photoUri,
    {
      maxDimension: width,
      minDimension: Math.min(400, width),
      targetBytes: 1_200_000,
      hardMaxBytes: 4 * 1024 * 1024,
    },
    { rotateDegrees: rotateDegrees || undefined },
  )
  return bytesToBase64(bytes)
}

async function fileToBase64(uri: string): Promise<string> {
  return uriToBase64(uri)
}

async function uriToBase64(uri: string): Promise<string> {
  const dataUriPayload = parseDataUri(uri)
  if (dataUriPayload) return dataUriPayload.base64

  if (Platform.OS !== 'web') return bytesToBase64(await readUriBytes(uri))

  const response = await fetch(uri)
  if (!response.ok) {
    throw new ServiceError({
      message: 'No se pudo leer el archivo seleccionado.',
      code: 'DOCUMENT_FILE_READ_FAILED',
      statusCode: 400,
    })
  }

  return bytesToBase64(new Uint8Array(await response.arrayBuffer()))
}

function isPdfDocument(uri: string, mimeType?: string, fileName?: string): boolean {
  return (
    mimeType === 'application/pdf' ||
    uri.toLowerCase().split('?')[0]?.endsWith('.pdf') ||
    fileName?.toLowerCase().endsWith('.pdf') === true
  )
}

function parseDataUri(uri: string): DocumentPayload | null {
  const match = uri.match(/^data:([^;,]+);base64,(.*)$/)
  if (!match) return null

  const mimeType = normalizeDocumentMimeType(match[1])
  const base64 = match[2]?.trim()
  if (!mimeType || !base64) return null

  return {
    base64,
    mimeType,
    filename: mimeType === 'application/pdf' ? 'document.pdf' : undefined,
  }
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  // expo-crypto's native digest (Android, SDK 54) expects a TypedArray, exactly
  // as its own docs show. A raw ArrayBuffer threw on the native module and broke
  // the document-verification upload — the same defect that stranded vehicle
  // photo uploads. Pass the Uint8Array view.
  const digest = await Crypto.digest(Crypto.CryptoDigestAlgorithm.SHA256, new Uint8Array(bytes))
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

function extensionForMimeType(mimeType: DocumentPayloadMimeType): string {
  if (mimeType === 'application/pdf') return 'pdf'
  if (mimeType === 'image/png') return 'png'
  if (mimeType === 'image/gif') return 'gif'
  if (mimeType === 'image/webp') return 'webp'
  return 'jpg'
}

function normalizeDocumentMimeType(value: string | undefined): DocumentPayloadMimeType | null {
  switch (value?.toLowerCase()) {
    case 'image/jpg':
    case 'image/jpeg':
      return 'image/jpeg'
    case 'image/png':
      return 'image/png'
    case 'image/gif':
      return 'image/gif'
    case 'image/webp':
      return 'image/webp'
    case 'application/pdf':
      return 'application/pdf'
    default:
      return null
  }
}

async function documentToPayload(
  uri: string,
  width: number,
  mimeType?: string,
  fileName?: string,
  rotateDegrees = 0,
): Promise<DocumentPayload> {
  const dataUriPayload = parseDataUri(uri)
  if (dataUriPayload) {
    return {
      ...dataUriPayload,
      filename:
        fileName ??
        dataUriPayload.filename ??
        (dataUriPayload.mimeType === 'application/pdf' ? 'document.pdf' : undefined),
    }
  }

  if (isPdfDocument(uri, mimeType, fileName)) {
    return {
      base64: await fileToBase64(uri),
      mimeType: 'application/pdf',
      filename: fileName ?? 'document.pdf',
    }
  }

  return {
    base64: await photoToBase64(uri, width, rotateDegrees),
    mimeType: 'image/jpeg',
    filename: fileName,
  }
}

async function readEdgeFunctionErrorBody(err: unknown): Promise<EdgeFunctionErrorBody> {
  const ctx = (
    err as {
      context?: {
        status?: number
        clone?: () => {
          json?: () => Promise<unknown>
          text?: () => Promise<string>
        }
      }
    }
  ).context
  if (!ctx || typeof ctx.clone !== 'function') return { message: null, reasons: [] }

  try {
    const body = (await ctx.clone().json?.()) as {
      error?: unknown
      message?: unknown
      reasons?: unknown
    }
    const reasons = Array.isArray(body?.reasons)
      ? body.reasons.filter((reason): reason is string => typeof reason === 'string')
      : []
    return {
      message:
        reasons[0] ??
        (typeof body?.error === 'string' ? body.error : null) ??
        (typeof body?.message === 'string' ? body.message : null),
      reasons,
      statusCode: ctx.status,
    }
  } catch {
    try {
      const text = await ctx.clone().text?.()
      return {
        message: text ? text.slice(0, 240) : null,
        reasons: [],
        statusCode: ctx.status,
      }
    } catch {
      return { message: null, reasons: [], statusCode: ctx.status }
    }
  }
}

async function serviceErrorFromFunctionError(
  error: { message: string; code?: string; status?: number },
  code: string,
): Promise<ServiceError> {
  const serverBody = await readEdgeFunctionErrorBody(error)
  return new ServiceError({
    message: serverBody.message ?? error.message,
    code,
    statusCode: error.status ?? serverBody.statusCode ?? 500,
    details: { reasons: serverBody.reasons },
    cause: error,
  })
}

class VehicleCardServiceImpl {
  /**
   * Extract data from a tarjeta de circulación photo using Claude Vision.
   * Resizes to 1024px, encodes as base64, sends to Edge Function.
   */
  async extractFromPhoto(photoUri: string): Promise<ExtractedVehicleCard> {
    // Try the card the right way up, then the other way up.
    //
    // A host photographing a landscape document with an upright phone is not a
    // mistake to be corrected with better instructions — it is the normal thing
    // to do, and it used to cost them the listing: the sideways image produced a
    // partial extraction, the tarjeta was filed `pending` instead of `approved`,
    // and the vehicle could never pass the activation gate. Nothing told anyone.
    //
    // The FIRST candidate is the deterministic fix (portrait -> landscape); the
    // second exists because a portrait frame cannot say whether the card was
    // turned clockwise or counter-clockwise. We only pay for the second call on
    // images that actually came back empty.
    const candidates = await orientationCandidates(photoUri)
    let lastError: unknown = null

    for (const rotateDegrees of candidates) {
      const base64 = await photoToBase64(photoUri, 1024, rotateDegrees)

      const { data, error } = await supabase.functions.invoke('extract-vehicle-card', {
        body: { imageBase64: base64, mimeType: 'image/jpeg' },
      })

      if (error) {
        lastError = await serviceErrorFromFunctionError(error, 'EXTRACTION_FAILED')
        // A transport/provider failure says nothing about orientation, so
        // rotating and asking again would just burn the rate limit.
        throw lastError
      }
      if (!data || data.error) {
        lastError = new ServiceError({
          message: data?.error ?? 'Extraction failed',
          code: 'EXTRACTION_FAILED',
          statusCode: 422,
        })
        continue
      }

      const extracted = data as ExtractedVehicleCard
      if (hasUsableVehicleCardFields(extracted)) return extracted
      // Read SOMETHING but nothing identifying: the classic signature of text the
      // model could see but not orient. Worth one more look from the other side.
      lastError = new ServiceError({
        message: 'Extraction returned no identifying fields',
        code: 'EXTRACTION_FAILED',
        statusCode: 422,
      })
    }

    throw lastError instanceof ServiceError
      ? lastError
      : new ServiceError({
          message: 'Extraction failed',
          code: 'EXTRACTION_FAILED',
          statusCode: 422,
        })
  }

  async extractInsurancePolicyFromPhoto(
    photoUri: string,
    mimeType?: string,
    fileName?: string,
  ): Promise<ExtractedInsurancePolicy> {
    const payload = await documentToPayload(photoUri, 1400, mimeType, fileName)

    const { data, error } = await supabase.functions.invoke('extract-insurance-policy', {
      body: { imageBase64: payload.base64, mimeType: payload.mimeType, filename: payload.filename },
    })

    if (error) throw await serviceErrorFromFunctionError(error, 'POLICY_EXTRACTION_FAILED')
    if (!data || data.error) {
      throw new ServiceError({
        message: data?.error ?? 'Insurance policy extraction failed',
        code: 'POLICY_EXTRACTION_FAILED',
        statusCode: 422,
      })
    }

    return data as ExtractedInsurancePolicy
  }

  async verifyAndStoreDocument(input: VerifyDocumentInput): Promise<void> {
    // Try every orientation before giving up. A sideways or upside-down card
    // used to write a pending row and leave the listing unpublishable. The
    // edge function only stores on success, so a failed attempt writes nothing
    // and a later orientation can be the one that approves.
    const candidates =
      input.documentType === 'insurance_policy' ? [0] : await orientationCandidates(input.photoUri)
    let lastError: ServiceError | null = null

    for (const rotateDegrees of candidates) {
      const payload = await documentToPayload(
        input.photoUri,
        input.documentType === 'insurance_policy' ? 1400 : 1200,
        input.mimeType,
        input.fileName,
        rotateDegrees,
      )

      const { data, error } = await supabase.functions.invoke('verify-vehicle-document', {
        body: {
          vehicleId: input.vehicleId,
          documentType: input.documentType,
          imageBase64: payload.base64,
          mimeType: payload.mimeType,
          filename: payload.filename,
          captureSource: input.captureSource,
          capturedAt: input.capturedAt,
          expected: input.expected ?? {},
        },
      })

      if (error) {
        lastError = await serviceErrorFromFunctionError(error, 'DOCUMENT_VERIFICATION_FAILED')
        if (lastError.statusCode !== 422) throw lastError
        continue
      }
      if (!data || data.error) {
        const reasons = Array.isArray(data?.reasons)
          ? data.reasons.filter((reason: unknown): reason is string => typeof reason === 'string')
          : []
        lastError = new ServiceError({
          message: reasons[0] ?? data?.error ?? 'Document verification failed',
          code: 'DOCUMENT_VERIFICATION_FAILED',
          statusCode: 422,
          details: { reasons },
        })
        continue
      }
      return
    }

    throw (
      lastError ??
      new ServiceError({
        message: 'Document verification failed',
        code: 'DOCUMENT_VERIFICATION_FAILED',
        statusCode: 422,
      })
    )
  }

  async uploadPendingDocument(input: PendingDocumentInput): Promise<void> {
    const [primaryOrientation] = input.documentType.startsWith('tarjeta_circulacion')
      ? await orientationCandidates(input.photoUri)
      : [0]
    const payload = await documentToPayload(
      input.photoUri,
      1920,
      input.mimeType,
      input.fileName,
      primaryOrientation,
    )
    const bytes = base64ToBytes(payload.base64)
    const documentSha256 = await sha256Hex(bytes)
    const uuid = Crypto.randomUUID()
    const extension = extensionForMimeType(payload.mimeType)
    const storagePath = `${input.vehicleId}/documents/${input.documentType}-${uuid}.${extension}`

    const { error: uploadError } = await uploadBytesWithSessionRecovery(
      'vehicle-documents',
      storagePath,
      bytes,
      {
        contentType: payload.mimeType,
        upsert: false,
      },
    )

    if (uploadError) throw ServiceError.fromSupabaseError(uploadError)
    await assertObjectStored('vehicle-documents', storagePath)

    const status = input.status ?? 'pending'
    const { error: documentError } = await supabase.from('vehicle_documents').insert({
      vehicle_id: input.vehicleId,
      document_type: input.documentType,
      document_number: input.documentNumber || null,
      expiry_date: validIsoDate(input.expiryDate),
      storage_path: storagePath,
      capture_source: input.captureSource ?? 'legacy_upload',
      captured_at: input.capturedAt ?? null,
      document_sha256: documentSha256,
      verification_payload: {
        source: input.captureSource ?? 'legacy_upload',
        capturedAt: input.capturedAt ?? null,
        mimeType: payload.mimeType,
        filename: payload.filename ?? null,
      },
      status,
      verified_at: status === 'approved' ? new Date().toISOString() : null,
    })

    if (documentError) throw ServiceError.fromSupabaseError(documentError)
  }

  /**
   * Upload a document photo to Supabase Storage.
   * Returns the public URL.
   */
  async uploadDocumentPhoto(
    vehicleId: string,
    photoUri: string,
    side: 'front' | 'back',
  ): Promise<string> {
    const compressed = await compressImage(photoUri, 'document')
    const uuid = Crypto.randomUUID()
    const storagePath = `${vehicleId}/documents/${side}-${uuid}.jpg`

    const { error: uploadError } = await uploadBytesWithSessionRecovery(
      'vehicle-documents',
      storagePath,
      compressed.bytes,
      {
        contentType: 'image/jpeg',
        upsert: false,
      },
    )

    if (uploadError) throw ServiceError.fromSupabaseError(uploadError)
    await assertObjectStored('vehicle-documents', storagePath)

    const { error: documentError } = await supabase.from('vehicle_documents').insert({
      vehicle_id: vehicleId,
      document_type: `tarjeta_circulacion_${side}`,
      storage_path: storagePath,
      status: 'pending',
    })

    if (documentError) throw ServiceError.fromSupabaseError(documentError)

    // `vehicle-documents` is private — a public URL here always 400s.
    return signedUrl('vehicle-documents', storagePath)
  }
}

export const vehicleCardService = new VehicleCardServiceImpl()
